/**
 * Slice to handle CRUD operation for dedicated receiving page.
 *
 * @version 1.0 18th July 2024
 * @author pTalan
 */
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import agent from "../../api/agent";
import {ApiResponse, DdStorePerformanceUIResponse, StoreManifestUIResponse} from "../../models/ApiResponse";
import { DdStorePerformanceResponse } from "../../models/responseModels/DdStorePerformanceResponse";
import { StoreManifestArgs } from "../../../pages/Manifest/ManifestConstant";
import { StoreManifestData, StoreManifestResponse } from "../../models/responseModels/StoreManifestResponse";
import { AddStorePkgRequest } from "../../models/requestModels/storePkgRequest";

// type required for initial state.
type InitialState = {
  isStoreManifestLoading: boolean;
  ddStores: DdStorePerformanceResponse[];
  storeManifestRes: StoreManifestResponse;
  collectiveManifestData: Record<string, StoreManifestData[]>;
  collectiveManifestDataImport: Record<string, StoreManifestData[]>;
  errorMessage: string | null;
};

// initialize initial state
const initialState: InitialState = {
  isStoreManifestLoading: false,
  ddStores: [],
  storeManifestRes: {
    storeManifestData: [], 
    pageSize: 0, 
    pageIndex: 0, 
    totalStoreManifest: 0 
  },
  collectiveManifestData: {},
  collectiveManifestDataImport: {},
  errorMessage: null,
};

// get store manifest data information from server.
export const getAllDdStores = createAsyncThunk<DdStorePerformanceUIResponse>(
  'store/all',
  async () => {
    const response = await agent.StoreManifestAgent.getAllDdStore();
    return response;
  }
);

// add new store package for the partictual store.
export const addStorePackage = createAsyncThunk<ApiResponse, AddStorePkgRequest>(
  'store/manifest/new/package',
  async (storePkgReq : AddStorePkgRequest) => {
    const response = await agent.StoreManifestAgent.submitNewStorePacakge(storePkgReq);
    return response;
  }
);

// get store manifest data information from server.
export const getManifestData = createAsyncThunk<StoreManifestUIResponse, StoreManifestArgs>(
  'store/manifest',
  async (storeManifestArgs : StoreManifestArgs) => {
    const response = await agent.StoreManifestAgent.getStoreManifestData(storeManifestArgs);
    return response;
  }
);

const storeManifestSlice = createSlice({
  name: "storeManifest",
  initialState,
  reducers: {
    updateStoreManifestList: (state, action) => {
      state.storeManifestRes.storeManifestData = action.payload;
    },
    updateCollectiveManifestData: (state, action) => {
      const { key, currentPageManifest } = action.payload;
      state.collectiveManifestData[key] = currentPageManifest;
    },
    clearCollectiveManifestData: (state) => {
      state.collectiveManifestData = {};
    },
    updateCollectiveManifestDataImport: (state, action) => {
      const { key, currentPageManifest } = action.payload;
      state.collectiveManifestDataImport[key] = currentPageManifest;
    },
    clearCollectiveManifestDataImport: (state) => {
      state.collectiveManifestDataImport = {};
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getAllDdStores.pending, (state) => {
      state.isStoreManifestLoading = true;
      })
      .addCase(getAllDdStores.fulfilled, (state, action) => {
      state.isStoreManifestLoading = false;
      if (action.payload.isSuccess) {
        state.ddStores = action.payload.data;
      } else {
        state.ddStores = [];
      }
      })
      .addCase(getAllDdStores.rejected, (state, action) => {
      state.isStoreManifestLoading = false;
      state.ddStores = [];
      state.errorMessage = action.error.message!;
       })
      .addCase(addStorePackage.pending, (state) => {
        state.isStoreManifestLoading = true;
      })
      .addCase(addStorePackage.fulfilled, (state) => {
        state.isStoreManifestLoading = false;
      })
      .addCase(addStorePackage.rejected, (state) => {
        state.isStoreManifestLoading = false;
      })
      .addCase(getManifestData.pending, (state) => {
        state.isStoreManifestLoading = true;
      })
      .addCase(getManifestData.fulfilled, (state, action) => {
        state.isStoreManifestLoading = false;
        if (action.payload.isSuccess) {
          state.storeManifestRes = action.payload.data;
        } else {
          state.storeManifestRes = {
            storeManifestData: [], 
            pageSize: 0, 
            pageIndex: 0, 
            totalStoreManifest: 0 
          };
        }
      })
      .addCase(getManifestData.rejected, (state, action) => {
        state.isStoreManifestLoading = false;
        state.storeManifestRes = {
          storeManifestData: [], 
          pageSize: 0, 
          pageIndex: 0, 
          totalStoreManifest: 0 
        };
        state.errorMessage = action.error.message!;
      });
  },
});

export default storeManifestSlice.reducer;
export const { 
  updateStoreManifestList,
  updateCollectiveManifestData,
  clearCollectiveManifestData,
  updateCollectiveManifestDataImport,
  clearCollectiveManifestDataImport } = storeManifestSlice.actions;
